<template>
  <div>
    <div class="relative">
      <v-json-editor v-model="inputValue"
                     ref="vJsonEditor"
                     :options="jsonEditorOptions"
                     :plus="false"
                     :height="height"
                     :class="{'disabled': isDisabled}"
      />
    </div>
    <input-error-message v-for="error in jsonEditorErrors"
                         :show-error-message="!!error.message"
                         :error-message="error.message"
                         :key="error.message"
    />
  </div>
</template>

<script>
import VJsonEditor from 'v-jsoneditor/src/index'
import tailwindConfig from '../../../tailwind.config'
import InputErrorMessage from '@/components/base-components/InputErrorMessage.vue'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import isEmpty from 'ramda/src/isEmpty'
import { JSON_EDITOR_MODE } from '@/constants/json-editor.constants'

export default {
  name: 'JsonInput',

  components: {
    InputErrorMessage,
    VJsonEditor
  },

  props: {
    value: {
      type: Object,
      required: true
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '250px'
    },
    mode: {
      type: String,
      default: JSON_EDITOR_MODE.CODE
    }
  },

  watch: {
    isErrorBubbleFocused: {
      immediate: false,
      handler(isErrorBubbleFocused) {
        if (!isErrorBubbleFocused) {
          // HACK: reset inline styled borderColor without re-rendering component or losing caret position
          const target = this.$refs.vJsonEditor.$refs.jsoneditor.childNodes[0]
          this.$refs.vJsonEditor.$parent.jsonEditorOptions.onFocus({ target })
        }
      }
    },

    jsonEditorErrors(errors) {
      this.$emit('update:json-validity', isEmpty(errors))
    }
  },

  data() {
    return {
      jsonEditorErrors: [],
      jsonEditorOptions: {
        mode: this.mode,
        mainMenuBar: false,
        navigationBar: false,
        statusBar: false,
        onFocus: ({ target }) => {
          target.style.borderColor = this.focusBorderColor
        },
        onBlur: ({ target }) => {
          target.style.borderColor = tailwindConfig.theme.colors[this.isErrorBubbleFocused ? 'focus-error' : 'soft']
        },
        onChange: async () => {
          this.jsonEditorErrors = await this.$refs.vJsonEditor.editor.validate()
        },
        onEditable: () => !this.isDisabled
      }
    }
  },

  computed: {
    inputValue: {
      get() {
        return this.value
      },
      async set(value) {
        this.$emit('input', value)
      }
    },

    isErrorBubbleFocused() {
      return chatBubbleModule.isErrorBubbleFocused()
    },

    focusBorderColor() {
      if (this.isDisabled) return null

      return tailwindConfig.theme.colors[this.isErrorBubbleFocused ? 'focus-error' : 'focus']
    }
  },

  methods: {
    async initEditor() {
      await this.$nextTick()
      const vJsonEditor = this.$refs.vJsonEditor
      if (!vJsonEditor?.editor) return
      vJsonEditor.editor.aceEditor.textInput.setReadOnly(this.isReadOnly || this.isDisabled)

      if (this.autofocus) {
        vJsonEditor.editor.focus()
      }
      if ([JSON_EDITOR_MODE.FORM, JSON_EDITOR_MODE.TREE].includes(this.mode)) {
        vJsonEditor.editor.expandAll()
      }
    }
  },

  mounted() {
    this.initEditor()
  }
}
</script>

<style lang="scss">
.jsoneditor-container {
  .ace_gutter {
    @apply hidden;
  }

  .ace_scroller {
  }

  .jsoneditor-mode-code {
    @apply border-soft focus:border-focus;
    @apply rounded;
    @apply pl-3 pt-2;
  }
}

.jsoneditor-container.disabled {
  .ace_active-line {
    background-color: white;
  }

  .ace_line, .ace_paren, .ace_parenl, .ace_variable, .ace_string, .ace_punctuation, .ace_operator {
    @apply text-gray;
  }

  .ace_bracket, .ace_cursor {
    border: none;
  }

  .ace_selection {
    @apply bg-primary-light;
  }
}
</style>
